import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from "react-router-dom";
import swal from 'sweetalert';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckCircle, faChevronLeft, faHourglassHalf, faLock, faRedoAlt, faTrashAlt, faUserCircle } from '@fortawesome/free-solid-svg-icons';

import Button from '../../Button';
import MagnetboardMain from '../magnetboard/MagnetboardMain';
import SupervisorPill from './reusable-components/SupervisorPill';
import { calculateConflicts, clearSectionPlacement, getSchoolRunningProcesses, runSectionPlacement } from '../../../js/Helpers';
import { FileUpload } from '../../../js/upload';
import { userHasManualPlacementPermission } from '../../../js/departmentResourcePermissions';
import { filterOutConflicts } from '../../../js/Conflicts';
import { useConflictActions, useConflictData } from '../store/magnetboardStore';

import '../../../css/hsms/place_sections.css';
import '../../../css/hsms/section_creation.css';

const MONITOR_AFTER_SUBSCREEN = 'clean_duplicates';
const MONITOR_BEFORE_SUBSCREEN = 'place_sections';
const SUPERVISOR_ROLE_ID = '3';

const MONITOR_STEP_LABELS = {
	manual_placements: 'Manual Placements',
	teacher_availability: 'Teacher Availability',
	course_availability: 'Course Availability',
	course_caps: 'Course Caps',
	course_lengths: 'Course Lengths',
	course_flags: 'Course Flags',
	course_teachers: 'Course Teachers',
	classroom_settings: 'Classroom Settings',
	course_classrooms: 'Course Classrooms',
	teacher_classrooms: 'Teacher Classrooms',
	create_sections: 'Create Sections',
};

const getStepOrder = (step = {}) => {
	const parsed_order = parseInt(step.subscreen_order || step.screen_order, 10);
	return Number.isNaN(parsed_order) ? 999 : parsed_order;
};

const getMonitorStepDefinitions = (appSubscreens = [], rolePages = {}) => {
	const source_steps = Array.isArray(appSubscreens) && appSubscreens.length > 0 ?
		appSubscreens :
		Object.entries(rolePages || {}).reduce((results, [screen, screen_info]) => {
			const subscreens = Array.isArray(screen_info?.subscreens) ? screen_info.subscreens : [];

			subscreens.forEach((subscreen_info, subscreen_index) => {
				results.push({
					...subscreen_info,
					screen,
					subscreen_index,
					screen_order: screen_info.screen_order,
				});
			});

			return results;
		}, []);

	const all_steps = source_steps.map((step, index) => ({
		...step,
		subscreen_index: step.subscreen_index ?? index,
	})).sort((step_a, step_b) => {
		if(getStepOrder(step_a) !== getStepOrder(step_b)) return getStepOrder(step_a) - getStepOrder(step_b);
		return step_a.subscreen_index - step_b.subscreen_index;
	});

	const anchor_index = all_steps.findIndex(step => step.subscreen === MONITOR_AFTER_SUBSCREEN);
	if(anchor_index === -1) return [];
	const stop_index = all_steps.findIndex(step => step.subscreen === MONITOR_BEFORE_SUBSCREEN);

	return all_steps.filter((step, step_index) => (
		step_index > anchor_index &&
		(stop_index === -1 || step_index < stop_index) &&
		`${step.responsible_role_id}` === SUPERVISOR_ROLE_ID &&
		step.subscreen !== MONITOR_BEFORE_SUBSCREEN
	));
};

const getMonitorSteps = (department = {}, monitor_step_definitions = []) => {
	return monitor_step_definitions.map(step_definition => ({
		...step_definition,
		...(department.subscreens?.[step_definition.subscreen] || {}),
		screen: step_definition.screen,
		subscreen: step_definition.subscreen,
	}));
};

const getMonitorStepLabel = (step = {}) => (
	MONITOR_STEP_LABELS[step.subscreen] || step.subscreen?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Next Step'
);

const SECTION_PLACEMENT_CONFLICTS = ['course_exceeds_capacity', 'course_period_restriction', 'course_outside_normal_room', 'teacher_missing', 'teacher_period_restriction', 'teacher_student_restriction', 'student_student_restriction', 'teacher_double_booked', 'classroom_double_booked', 'student_double_booked', 'teacher_conflicting_periods', 'classroom_conflicting_periods', 'student_conflicting_periods', 'teacher_too_many_sections', 'teacher_too_many_sections_in_a_row', 'classroom_missing'];
const CLEARING_LOADER_COLORS = ['pastel-blue', 'yellow', 'pastel-green', 'pastel-light-blue', 'yellow', 'pastel-blue', 'pastel-light-blue', 'pastel-green', 'yellow'];

export default function PlaceSections({ permissionChecked, schoolSubscribed, subscreenRestricted, getNewData, departmentsLoading, departments, departmentID, sortedDepartments, changeDepartment, userInfo, schoolInfo, schoolType, schoolInfoLoading, setSchoolInfoLoading, scheduleInfo, scheduleInfoLoading, setScheduleInfo, scheduleStructureData, setScheduleStructureData, scheduleStructureDataLoading, lunchInfo, coursesLoading, courses, setCourses, teachersLoading, teachers, setTeachers, classroomsLoading, classrooms, studentsLoading, students, setStudents, sectionsLoading, sections, setSections, subsections, setSubsections, labels, setLabels, usersLoading, userIsAdmin, submitDepartment, changesSavedMessage, setChangesSavedMessage, showNextButton, appSubscreens, rolePages, handleMoveToNextScreen, subscreenSelect, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo }) {
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(true);
	const [placementsHaveStarted, setPlacementsHaveStarted] = useState(false);
	const [placementsGenerated, setPlacementsGenerated] = useState(false);
	const [placementClearInProgress, setPlacementClearInProgress] = useState(false);
	const [statusCheckComplete, setStatusCheckComplete] = useState(false);
	const { setConflicts, markConflictDataFresh } = useConflictActions();
	const { conflicts } = useConflictData();
	const placementClearedStorageKey = `section_placement_cleared_${schoolInfo.current_schedule_version_id || 'current'}`;
	const monitorStepDefinitions = useMemo(() => getMonitorStepDefinitions(appSubscreens, rolePages), [appSubscreens, rolePages]);

	const departmentProgressRows = useMemo(() => {
		return (Array.isArray(departments) ? departments : []).map(department => {
			const monitor_steps = getMonitorSteps(department, monitorStepDefinitions);
			const submitted_steps = monitor_steps.filter(step => !!step.submitted);
			const submitted_count = submitted_steps.length;
			const total_count = monitor_steps.length;
			const percent_complete = total_count > 0 ? Math.round((submitted_count / total_count) * 100) : 0;
			const next_step = monitor_steps.find(step => !step.submitted);
			const complete = total_count > 0 && submitted_count === total_count;

			return {
				department_id: department.department_id,
				department: department.department,
				complete,
				percent_complete,
				submitted_count,
				total_count,
				next_step,
				progress_color: complete ? 'green' : (percent_complete <= 50 ? 'red' : 'yellow'),
				status_label: complete ? 'Ready for placement' : getMonitorStepLabel(next_step),
			};
		});
	}, [departments, monitorStepDefinitions]);

	const allDepartmentsComplete = departmentProgressRows.length > 0 && departmentProgressRows.every(row => row.complete);
	const incompleteDepartmentCount = departmentProgressRows.filter(row => !row.complete).length;
	const placementIsRunning = placementsHaveStarted && !placementsGenerated;
	const loadingActiveScheduleYear = scheduleInfo?.app_current_schedule_year_id !== undefined && schoolInfo?.current_schedule_year_id !== undefined && `${scheduleInfo.app_current_schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`;
	const previousScheduleYearAutoSchedulingMessage = 'Sorry, you cannot run auto-scheduling features in previous scheduling years.';
	const placeSectionsDisabled = !loadingActiveScheduleYear || !allDepartmentsComplete || placementIsRunning || placementsGenerated || !userIsAdmin;
	const reviewDepartments = useMemo(() => {
		const ordered_departments = Array.isArray(sortedDepartments) && sortedDepartments.length > 0 ? sortedDepartments : departments;
		return ordered_departments;
	}, [departments, sortedDepartments]);
	const currentDepartment = departments.find(department => `${department.department_id}` === `${departmentID}`) || null;
	const currentDepartmentSupervisors = Array.isArray(currentDepartment?.supervisors) ? currentDepartment.supervisors : [];
	const currentDepartmentSubscreen = currentDepartment?.subscreens?.place_sections || null;
	const currentDepartmentSubmitted = currentDepartmentSubscreen ? !!currentDepartmentSubscreen.submitted : false;
	const currentDepartmentConfirmed = currentDepartmentSubscreen ? !!currentDepartmentSubscreen.confirmed : false;
	const screenCompleteForUser = currentDepartment ? (currentDepartmentConfirmed || (!userIsAdmin && currentDepartmentSubmitted)) : false;
	const currentUserCanEditBoard = userIsAdmin || userHasManualPlacementPermission(departments, userInfo, departmentID);
	const currentDepartmentSections = useMemo(() => {
		if(!departmentID) return [];
		return sections.filter(section => Array.isArray(section.departments) && section.departments.some(section_department_id => `${section_department_id}` === `${departmentID}`));
	}, [departmentID, sections]);
	const departmentCourseIDs = useMemo(() => {
		if(!departmentID || coursesLoading || sectionsLoading) return ['None'];

		const course_ids = courses.reduce((result, course) => {
			const course_in_department = Array.isArray(course.departments) && course.departments.some(course_department_id => `${course_department_id}` === `${departmentID}`);
			const course_has_section_in_department = currentDepartmentSections.some(section => `${section.course_id}` === `${course.course_id}`);

			if(course_in_department || course_has_section_in_department) result.push(course.course_id);

			return result;
		}, []);

		return course_ids.length > 0 ? course_ids : ['None'];
	}, [courses, coursesLoading, currentDepartmentSections, departmentID, sectionsLoading]);
	const departmentTeacherIDs = useMemo(() => {
		if(!departmentID || teachersLoading || sectionsLoading) return ['None'];

		const teacher_ids = teachers.reduce((result, teacher) => {
			const teacher_in_department = Array.isArray(teacher.departments) && teacher.departments.some(teacher_department_id => `${teacher_department_id}` === `${departmentID}`);
			const teacher_teaches_section_in_department = currentDepartmentSections.some(section => `${section.teacher_id}` === `${teacher.teacher_id}`);
			const teacher_is_secondary_on_department_section = currentDepartmentSections.some(section => Array.isArray(section.secondary_teachers) && section.secondary_teachers.some(teacher_id => `${teacher_id}` === `${teacher.teacher_id}`));

			if(teacher_in_department || teacher_teaches_section_in_department || teacher_is_secondary_on_department_section) result.push(teacher.teacher_id);

			return result;
		}, []);

		return teacher_ids.length > 0 ? teacher_ids : ['None'];
	}, [currentDepartmentSections, departmentID, sectionsLoading, teachers, teachersLoading]);
	const departmentClassroomIDs = useMemo(() => {
		if(!departmentID || classroomsLoading || sectionsLoading) return ['None'];

		const classroom_ids = classrooms.reduce((result, classroom) => {
			const classroom_in_department = Array.isArray(classroom.departments) && classroom.departments.some(classroom_department_id => `${classroom_department_id}` === `${departmentID}`);
			const classroom_has_section_in_department = currentDepartmentSections.some(section => `${section.classroom_id}` === `${classroom.classroom_id}`);

			if(classroom_in_department || classroom_has_section_in_department) result.push(classroom.classroom_id);

			return result;
		}, []);

		return classroom_ids.length > 0 ? classroom_ids : ['None'];
	}, [classrooms, classroomsLoading, currentDepartmentSections, departmentID, sectionsLoading]);

	const generateSchedule = async ({force = false} = {}) => {
		if(!loadingActiveScheduleYear) return;
		if(placeSectionsDisabled && !force) return;

		localStorage.removeItem(placementClearedStorageKey);
		setPlacementsHaveStarted(true);
		setPlacementsGenerated(false);
		setIsLoading(false);

		try {
			await runSectionPlacement({school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_version_id:schoolInfo.current_data_version_id});

			const fileUpload = new FileUpload(schoolInfo.current_data_version_id, schoolInfo.current_schedule_version_id, schoolInfo.school_id);
			const running_processes = await fileUpload.getSchoolProcesses(schoolInfo.current_schedule_year_id);
			const progress_info = running_processes.find(process => process.process_type === 'sectionplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);
			const progress_id = progress_info ? progress_info.progress_id : null;

			if(!progress_id) throw new Error('Section placement progress id was not found.');

			await fileUpload.checkUploading(schoolInfo.school_id, progress_id, async (event) => {
				if(event.failure)
				{
					setPlacementsHaveStarted(false);
					setPlacementsGenerated(false);
					setSchoolInfoLoading(false);
					setIsLoading(false);
					swal({
						icon: 'error',
						title: 'Error Placing Sections',
						text: 'We had an issue placing your sections. Please try again.',
						dangerMode: true,
					});
					return;
				}

				if(!event.done) return;

				getNewData();
				setPlacementsGenerated(true);
				setSchoolInfoLoading(false);
				setIsLoading(false);

				calculateConflicts({school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, conflicts_to_check:SECTION_PLACEMENT_CONFLICTS, update_all:true}).then((conflicts_response) => {
					const conflicts_list = Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : [];
					const filtered_out_conflicts = filterOutConflicts(conflicts, [], SECTION_PLACEMENT_CONFLICTS);
					setConflicts([...filtered_out_conflicts, ...conflicts_list]);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
				}).catch(error => {
					console.log(error.response || error);
				});
			});
		} catch(error)
		{
			console.log(error.response || error);
			setPlacementsHaveStarted(false);
			setPlacementsGenerated(false);
			setSchoolInfoLoading(false);
			setIsLoading(false);
			swal({
				icon: 'error',
				title: 'Error Placing Sections',
				text: 'We had an issue placing your sections. Please try again.',
				dangerMode: true,
			});
		}
	};

	const clearPlacement = async ({rerun = false} = {}) => {
		if(!userIsAdmin || placementIsRunning) return;

		const options =  {
			title: rerun ? 'Clear and rerun placement?' : 'Clear section placement?',
			text: rerun ? 'This will clear section placements and immediately rerun automatic section placement. This will not clear any manually placed sections or any locked sections.' : 'This will clear section placements so you can adjust constraints and place sections again. This will not clear any manually placed sections or any locked sections.',
			icon: 'warning',
			dangerMode: true,
			buttons: {
				cancel: {
					text: 'Cancel',
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: rerun ? 'Clear & Rerun' : 'Clear Placement',
					value: true,
					visible: true,
					className: rerun ? 'blue-btn' : 'red-btn'
				},
			}
		};

		const should_clear = await swal(options);
		if(!should_clear) return;

		setPlacementClearInProgress(true);
		setIsLoading(false);

		const new_sections = sections.map(section => {
			if(section.locked === '1' || section.is_manual === '1') return section;
			return {
				...section,
				classroom_id: null,
				course_periods: [],
				quarter_days: [],
				student_list: [],
			};
		});

		setSections([...new_sections]);
		sessionStorage.setItem('actionLog', JSON.stringify([]));
		sessionStorage.setItem('undoneActions', JSON.stringify([]));

		try {
			await clearSectionPlacement({school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id});

			calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				conflicts_to_check: SECTION_PLACEMENT_CONFLICTS,
				update_all: true,
			}).then((conflicts_response) => {
				const conflicts_list = Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : [];
				const filtered_out_conflicts = filterOutConflicts(conflicts, [], SECTION_PLACEMENT_CONFLICTS);
				setConflicts([...filtered_out_conflicts, ...conflicts_list]);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
			}).catch(error => {
				console.log(error.response || error);
			});

			getNewData();
			changeDepartment(null);
			setPlacementsGenerated(false);
			setPlacementsHaveStarted(false);
			localStorage.setItem(placementClearedStorageKey, '1');

			if(rerun)
			{
				setPlacementClearInProgress(false);
				await generateSchedule({force: true});
				return;
			}

			setPlacementClearInProgress(false);
			setIsLoading(false);
			setSchoolInfoLoading(false);
			swal({
				icon: 'success',
				title: 'Placement Cleared',
				text: 'You can adjust constraints and run section placement again.',
			});
		} catch(error)
		{
			console.log(error.response || error);
			setPlacementClearInProgress(false);
			setIsLoading(false);
			setSchoolInfoLoading(false);
			swal({
				icon: 'error',
				title: 'Unable to Clear Placement',
				text: 'We had an issue clearing section placement. Please try again.',
				dangerMode: true,
			});
		}
	};

	const goToEditConstraints = () => {
		if(typeof subscreenSelect === 'function')
		{
			subscreenSelect('teacher_availability', 'edit_constraints');
			return;
		}

		history.push(`/app/${schoolType || 'hsms'}/progress/edit_constraints/teacher_availability`);
	};

	useEffect(() => {
		setScreenAllowsUniversalSidebar(false);
		setUniversalSidebarLoadingInfo(null);
	}, [setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);

	useEffect(() => {
		if(!schoolInfo.school_id || !schoolInfo.current_schedule_year_id || !schoolInfo.current_schedule_version_id) return;

		if(localStorage.getItem(placementClearedStorageKey) === '1')
		{
			setPlacementsHaveStarted(false);
			setPlacementsGenerated(false);
			setStatusCheckComplete(true);
			return;
		}

		getSchoolRunningProcesses({school_id: schoolInfo.school_id, schedule_year_id:schoolInfo.current_schedule_year_id}).then(running_processes => {
			const progress_info = running_processes.find(process => process.process_type === 'sectionplace' && `${process.schedule_version_id}` === `${schoolInfo.current_schedule_version_id}`);

			if(progress_info?.is_done)
			{
				setPlacementsHaveStarted(true);
				setPlacementsGenerated(true);
			}
			else if(progress_info?.has_started)
			{
				setPlacementsHaveStarted(true);
			}

			setStatusCheckComplete(true);
		}).catch(error => {
			console.log(error);
			setStatusCheckComplete(true);
		});
	}, [placementClearedStorageKey, schoolInfo.school_id, schoolInfo.current_schedule_year_id, schoolInfo.current_schedule_version_id]);

	useEffect(() => {
		if(permissionChecked && !departmentsLoading && !usersLoading && !coursesLoading && !classroomsLoading && !teachersLoading && !sectionsLoading && statusCheckComplete) setIsLoading(false);
	}, [permissionChecked, departmentsLoading, usersLoading, coursesLoading, classroomsLoading, teachersLoading, sectionsLoading, statusCheckComplete]);

	const renderReviewDepartmentRows = () => (
		<div className='progress-choose-department-container place-sections-review-department-list'>
			{reviewDepartments.map((department_info, index) => {
				const department_subscreen = department_info?.subscreens?.place_sections || {};
				const department_submitted = !!department_subscreen.submitted;
				const department_confirmed = !!department_subscreen.confirmed;
				const user_is_supervisor = userIsAdmin || (department_info.supervisors || []).some(supervisor => `${supervisor.user_id}` === `${userInfo.user_id}`);
				const text_color = department_confirmed ? 'green' : (department_submitted ? 'yellow' : 'gray');
				const button_color = department_confirmed ? 'green' : (department_submitted ? 'bright-yellow' : 'dark-blue-gray');
				const status = department_confirmed ? 'Complete' : (department_submitted ? 'Awaiting Confirmation' : 'Ready to Review');
				const button_text = department_confirmed || department_submitted ? 'Review' : 'Start';

				return (
					<div className={`progress-choose-department-row ${!user_is_supervisor ? 'opacity-50' : ''}`} key={index}>
						<div className='progress-choose-department-name'>{department_info.department}</div>
						<div className={`progress-choose-department-status ${text_color}-text`}>
							<div>{status}</div>
							<FontAwesomeIcon className='progress-choose-department-status-icon' icon={faCheckCircle}/>
						</div>
						<div className='small-text text-align-right'>
							{user_is_supervisor ?
								(
									<div className={`progress-choose-department-btn btn ${button_color}-btn align-right`} onClick={() => changeDepartment(department_info.department_id)}>{button_text}</div>
								):
								(
									<div className={`progress-choose-department-btn progress-choose-department-btn-locked btn ${button_color}-btn align-right`}>
										<FontAwesomeIcon icon={faLock}/>
									</div>
								)
							}
						</div>
					</div>
				);
			})}
		</div>
	);

	const goToCurrentStep = (row) => {
		if(!row.next_step || !row.next_step.subscreen || !row.next_step.screen) return;
		if(typeof subscreenSelect !== 'function') return;

		subscreenSelect(row.next_step.subscreen, row.next_step.screen, row.department_id);
	};

	const renderClearingSectionsLoader = () => (
		<div className='section-review-missing-requests-container place-sections-clearing-container'>
			<div className='place-sections-clearing-loader' aria-hidden='true'>
				{CLEARING_LOADER_COLORS.map((color, index) => (
					<div className='place-sections-clearing-calendar' key={index}>
						<div className={`place-sections-clearing-calendar-top ${color}`}></div>
						<div className='place-sections-clearing-calendar-bottom'></div>
					</div>
				))}
			</div>
			<h2>Clearing section placements...</h2>
			<div>We are removing auto-placed sections while keeping manually placed and locked sections untouched.</div>
		</div>
	);

	return (
		<div>
			{placementClearInProgress ?
			(
				renderClearingSectionsLoader()
			):isLoading ?
			(
				<div className='center-text'>
					<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
				</div>
			):(subscreenRestricted) ?
			(
				<div className='general-screen-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>No Access</h1>
					<div>You do not have access to this screen</div>
				</div>
			):(!schoolSubscribed) ?
			(
				<div className='section-placement-screen-message' style={{maxWidth:'600px'}}>
					<img style={{height:'80px'}} src={require("../../../images/start-scheduling.png")} alt='Start scheduling'/>
					<h2>Place Your Sections</h2>
					<div>In this step, we will place all your remaining sections, taking into account all your constraints, to find the best place in the schedule for each section.</div>
					<div style={{padding:'20px', backgroundColor:'#efefef', border:'2px solid #ddd', margin:'20px 0px 20px 0px', borderRadius:'10px'}}>
						<div>You need a paid subscription for this feature.</div>
						<div style={{margin:'6px 0px 0px 0px'}}>But don't let that stop you. You can still skip this step and keep working.</div>
						<div style={{fontSize:'18px', margin:'45px 0px 0px 0px'}}><strong>Please contact us at contact@edario.com to get full access in Edario.</strong></div>
					</div>
					<div className="blue-link align-right" style={{maxWidth:'250px', margin:'0px 0px -30px 0px'}} onClick={handleMoveToNextScreen}>Skip to Next Step</div>
				</div>
			):((placementsHaveStarted && !placementsGenerated)) ? (
				<div className='section-review-missing-requests-container'>
					<div id='custom-section-creation-loader-container'>
						<div className='custom-section-creation-loader-column'>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top pastel-blue'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top pastel-blue'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
						</div>
						<div className='custom-section-creation-loader-column'>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top yellow'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top yellow'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top yellow'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
						</div>
						<div className='custom-section-creation-loader-column'>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top pastel-green'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
						</div>
						<div className='custom-section-creation-loader-column'>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top pastel-light-blue'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
							<div className='custom-section-creation-loader-row'>
								<div className='custom-section-creation-loader-row-top pastel-light-blue'></div>
								<div className='custom-section-creation-loader-row-bottom'></div>
							</div>
						</div>
					</div>
					<h2>Sections are currently being placed...</h2>
					<div>We are currently calculating and assigning section placements. Please check back shortly to review your schedule.</div>
				</div>
			):(placementsGenerated && departmentID && currentDepartment) ? (
				<>
					<h1 className='progress-main-header'>Place Sections</h1>
					<p className='progress-subheader'>{currentUserCanEditBoard ? `Review section placement for ${currentDepartment.department} and make any needed adjustments.` : `Review section placement for ${currentDepartment.department}. Editing is disabled for this department.`}</p>
					<div id='manual-placement-container'>
						<div id='manual-placement-top-bar-container'>
							<div className='manual-placement-top-bar-inner-container'>
								<div className='department-supervisor-parent-container' style={{margin:'10px 0px -5px 0px'}}>
									{currentDepartmentSupervisors.length === 0 &&
										<div className='department-supervisor-inner-container'>
											<FontAwesomeIcon className='department-supervisor-user-icon' icon={faUserCircle}/>
											<div className='department-supervisor-name'>No Supervisor Assigned</div>
										</div>
									}
									{currentDepartmentSupervisors.map((supervisor, index) => (
										<SupervisorPill
											supervisor={supervisor}
											canRemove={false}
											onRemove={() => {}}
											key={index}
										/>
									))}
								</div>
							</div>
							<div id='section-placement-submit-btn-container'>
								{!screenCompleteForUser &&
									<Button key={departmentID} classes={['mark-as-complete-btn']} selected={false} text={`${!currentDepartmentSubmitted ? 'Mark As Complete' : 'Confirm Department'}`} icon={faCheck} iconPosition='left' onClickFunction={() => submitDepartment(departmentID)} />
								}
								{changesSavedMessage &&
									<div className='all-changes-saved-message' style={{margin:'20px 0px 0px 0px'}}>
										<FontAwesomeIcon className='green-text' icon={faCheckCircle}/>
										<div>
											<h5>Changes Saved</h5>
											<div className='small-text'>All your changes have been saved</div>
										</div>
									</div>
								}
							</div>
						</div>
						<div className='section-placement-content'>
							<div>
								<FontAwesomeIcon icon={faChevronLeft} className='progress-back-to-departments-btn' onClick={()=>changeDepartment(null)} style={{marginTop:'45px'}}/>
							</div>
							<div className='section-placement-content-inner-content'>
								<MagnetboardMain schoolSubscribed={schoolSubscribed} schoolInfoLoading={schoolInfoLoading} schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} setScheduleInfo={setScheduleInfo} scheduleStructureData={scheduleStructureData} setScheduleStructureData={setScheduleStructureData} scheduleInfoLoading={scheduleInfoLoading} scheduleStructureDataLoading={scheduleStructureDataLoading} userInfo={userInfo} departmentsLoading={departmentsLoading} departments={departments} teachersLoading={teachersLoading} teachers={teachers} setTeachers={setTeachers} coursesLoading={coursesLoading} courses={courses} setCourses={setCourses} classroomsLoading={classroomsLoading} classrooms={classrooms} studentsLoading={studentsLoading} students={students} setStudents={setStudents} sectionsLoading={sectionsLoading} sections={sections} setSections={setSections} subsections={subsections} setSubsections={setSubsections} lunchInfo={lunchInfo} labels={labels} setLabels={setLabels} setScreenAllowsUniversalSidebar={setScreenAllowsUniversalSidebar} magnetboardOptions={{show_top_bar:true, allow_schedule_name_editing:false, allow_filtering:false, show_match_percent:false, show_conflicts:true, show_menu:true, hide_edit:true, hide_duplicate_schedule:true, hide_delete_schedule:true, hide_export_schedule:true, hide_actions:true, hide_reports:true, allow_new_sections:currentUserCanEditBoard, allow_placeholders:false, allow_hotlinks:false, allow_section_dragging:currentUserCanEditBoard, allow_section_structure_editing:currentUserCanEditBoard, allow_section_roster_editing:currentUserCanEditBoard, allow_locked_section_roster_editing:false, allow_section_duplicate:currentUserCanEditBoard, allow_section_delete:currentUserCanEditBoard, allow_inline_quarter_editing:currentUserCanEditBoard, allow_period_availability_editing:false, current_department_id:departmentID, current_department:currentDepartment.department, allowed_teacher_ids:departmentTeacherIDs, allowed_course_ids:departmentCourseIDs, allowed_classroom_ids:departmentClassroomIDs, view_options:[{value:'teacher', display:'Teacher View'}, {value:'course', display:'Course View'}, {value:'classroom', display:'Classroom View'}]}}/>
							</div>
						</div>
					</div>
				</>
				):(placementsGenerated) ? (
					<>
						<div className='place-sections-review-container'>
							<div className='place-sections-review-main'>
								<div className='progress-choose-department-screen place-sections-review-picker'>
									{userIsAdmin &&
										<div className='place-sections-review-heading'>
											<div className='place-sections-success-icon'>
												<FontAwesomeIcon icon={faCheckCircle}/>
											</div>
											<div>
												<h2>Sections Have Been Placed</h2>
												<p>Select a department below to review placement. Once departments look right, move on to placing students.</p>
											</div>
										</div>
									}
									{userIsAdmin &&
										<div className='place-sections-controls-row'>
											<div className='place-sections-recovery-action' onClick={goToEditConstraints}>
												<div className='place-sections-recovery-action-icon blue-tone'>
													<FontAwesomeIcon icon={faChevronLeft}/>
												</div>
												<div>
													<strong>Revise Constraints</strong>
													<span>Go back to Teacher Availability.</span>
												</div>
											</div>
											<div className='place-sections-recovery-action' onClick={() => clearPlacement()}>
												<div className='place-sections-recovery-action-icon red-tone'>
													<FontAwesomeIcon icon={faTrashAlt}/>
												</div>
												<div>
													<strong>Clear Placements</strong>
													<span>Clear auto-placed sections.</span>
												</div>
											</div>
											<div className='place-sections-recovery-action' onClick={() => clearPlacement({rerun: true})}>
												<div className='place-sections-recovery-action-icon green-tone'>
													<FontAwesomeIcon icon={faRedoAlt}/>
												</div>
												<div>
													<strong>Clear &amp; Rerun</strong>
													<span>Clear and run placement again.</span>
												</div>
											</div>
										</div>
									}
									<div className='progress-choose-department-heading'>Review departments:</div>
									{renderReviewDepartmentRows()}
								</div>
							</div>
						</div>
						{showNextButton && userIsAdmin &&
							<div className='fixed-bottom-save-container'>
								<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Place Students' onClickFunction={handleMoveToNextScreen} />
							</div>
						}
					</>
				):(!userIsAdmin) ? (
				<div className='general-screen-message place-sections-pending-message'>
					<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
					<h1>Placement Review Locked</h1>
					<div>Once the master scheduler has run the schedule, it should show up here for review.</div>
				</div>
			):(
				<div className='place-sections-monitor-container'>
					<div className='place-sections-monitor-header'>
						<div>
							<h2>Department Progress</h2>
							<p>The scheduler will only place sections after all departments have completed their constraints and submitted their sections.</p>
						</div>
						<div className={`place-sections-monitor-summary ${allDepartmentsComplete ? 'complete' : 'waiting'}`}>
							<FontAwesomeIcon icon={allDepartmentsComplete ? faCheckCircle : faHourglassHalf}/>
							<div>
								<div className='place-sections-monitor-summary-value'>{allDepartmentsComplete ? 'Ready' : `${incompleteDepartmentCount} Working`}</div>
								<div className='place-sections-monitor-summary-label'>{departmentProgressRows.length} Departments</div>
							</div>
						</div>
					</div>
					<div className='place-sections-monitor-table'>
						<div className='place-sections-monitor-table-header'>
							<div>Department</div>
							<div>Current Step</div>
							<div>Submitted</div>
							<div>Status</div>
						</div>
						{departmentProgressRows.map(row => (
							<div className='place-sections-monitor-row' key={row.department_id}>
								<div className='place-sections-monitor-department-name'>{row.department}</div>
								<div>
									{row.complete ?
										(
											<div className='place-sections-monitor-step'>{row.status_label}</div>
										):
										(
											<div className='place-sections-monitor-step place-sections-monitor-step-link' onClick={() => goToCurrentStep(row)}>{row.status_label}</div>
										)
									}
								</div>
								<div className='place-sections-monitor-progress-cell'>
									<div className='place-sections-monitor-progress-meta'>
										<span>{row.percent_complete}%</span>
										<span>{row.submitted_count}/{row.total_count}</span>
									</div>
									<div className='place-sections-monitor-progress-track'>
										<div className={`place-sections-monitor-progress-fill ${row.progress_color}`} style={{width:`${row.percent_complete}%`}}></div>
									</div>
								</div>
								<div className={`place-sections-monitor-status ${row.complete ? 'complete' : 'working'}`}>
									<FontAwesomeIcon icon={row.complete ? faCheckCircle : faHourglassHalf}/>
									<span>{row.complete ? 'Complete' : 'In Progress'}</span>
								</div>
							</div>
						))}
					</div>
					<div className='place-sections-monitor-footer'>
						<div className='place-sections-monitor-footer-note'>
							{!loadingActiveScheduleYear ? previousScheduleYearAutoSchedulingMessage : (allDepartmentsComplete ? 'All departments are ready for section placement.' : 'Waiting for every department to submit all required steps.')}
						</div>
						<div className={`btn place-sections-monitor-action-btn ${placeSectionsDisabled ? 'btn-disabled' : 'green-btn'}`} onClick={generateSchedule}>Place Sections</div>
					</div>
				</div>
			)}
		</div>
	);
}
